/**
 * Table skeleton — placeholder rows that match the real table's column
 * widths. Used while the parent useQuery is loading the first page.
 *
 * Why row-shaped skeletons not a centered spinner: skeletons preserve
 * layout, so the page doesn't jump when data lands. Spinners imply "wait,
 * something might be wrong"; skeletons imply "rendering, almost there."
 */
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type Column = {
  /** Header label rendered as plain text. */
  label: string
  /** Tailwind width / visibility classes for the column wrapper. */
  className?: string
  /** Skeleton width inside the cell — controls visual rhythm. Default w-24. */
  bar?: string
}

type Props = {
  columns: Column[]
  rows?: number
}

export function TableSkeleton({ columns, rows = 5 }: Props) {
  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => (
              <TableHead key={c.label} className={c.className}>
                {c.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
              {columns.map((c, j) => (
                <TableCell key={`${i}-${j}`} className={c.className}>
                  <Skeleton className={`h-4 ${c.bar ?? 'w-24'}`} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
